import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  AsyncValidatorFn,
  ControlValueAccessor,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { LoyaltyProgramApiService } from '@features/loyalty-program/services/loyalty-program-api/loyalty-program-api.service';
import { AppSettingService } from '@rd/core/services/app-setting/app-setting.service';
import { LoyaltyProgram, User } from '@rd/types';
import { NAME_VALIDATION_REGEX } from '@rd/utils/regex.utils';
import dayjs from 'dayjs';
import { Observable, of, Subject, timer } from 'rxjs';
import { catchError, first, map, switchMap, takeUntil } from 'rxjs/operators';
import { TransferConnectV2SettingService } from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import { TransferConnectV2MembershipDetailFormData } from '../../types';
import { dateOfBirthValidator } from './validators/dateofbirth-validator';

@Component({
  selector: 'rd-tcv2-membership-detail-form',
  templateUrl: './membership-detail-form.component.html',
  styleUrls: ['./membership-detail-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TcV2MembershipDetailFormComponent),
      multi: true,
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => TcV2MembershipDetailFormComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class TcV2MembershipDetailFormComponent
  implements OnInit, OnDestroy, ControlValueAccessor, AsyncValidator
{
  @HostBinding('class.rd-tcv2-membership-detail-form') componentClass = true;

  @HostBinding('class.appearance-outline') get isAppearanceOutline(): boolean {
    return this.matFormFieldAppearance === 'outline';
  }

  @Input() disablePrefilledFields: boolean;
  @Input() loyaltyProgram: LoyaltyProgram;
  @Input() formData: TransferConnectV2MembershipDetailFormData;
  @Input() isSubmitted: Subject<boolean>;
  @Input() prefillForm: boolean;
  @Input() user: User;

  matFormFieldAppearance: MatFormFieldAppearance;
  private destroy$ = new Subject();
  showDobField: boolean;
  dateValidatorFn: any;

  propagateChange: (value: any) => {};
  membershipDetailForm: UntypedFormGroup;
  onTouched: () => void = () => {};

  constructor(
    private fb: UntypedFormBuilder,
    private loyaltyProgramApiService: LoyaltyProgramApiService,
    private cdRef: ChangeDetectorRef,
    private appSettingService: AppSettingService,
    private transferConnectV2SettingService: TransferConnectV2SettingService,
  ) {}

  ngOnInit(): void {
    this.matFormFieldAppearance = this.appSettingService.matFormFieldAppearance;
    this.dateValidatorFn = dateOfBirthValidator;
    this.showDobField =
      this.transferConnectV2SettingService.shouldShowDobFieldInLinkForm(
        this.loyaltyProgram.transferConnectIdentifier,
      );

    const { firstName, lastName, membershipNumber, dateOfBirth } =
      this.getInitialFields();

    this.membershipDetailForm = this.fb.group({
      membershipNumber: [
        membershipNumber,
        Validators.required,
        this.membershipNumberValidator(),
      ],
      firstName: [
        firstName,
        {
          validators: [Validators.pattern(NAME_VALIDATION_REGEX)],
        },
      ],
      lastName: [
        lastName,
        {
          validators: [Validators.pattern(NAME_VALIDATION_REGEX)],
        },
      ],
    });

    if (this.showDobField) {
      this.membershipDetailForm.addControl(
        'dateOfBirth',
        new UntypedFormControl(dateOfBirth, [
          Validators.required,
          dateOfBirthValidator(),
        ]),
      );
    }

    if (this.disablePrefilledFields) {
      Object.keys(this.membershipDetailForm.controls).forEach((control) => {
        if (control !== 'membershipNumber' && control !== 'dateOfBirth') {
          this.membershipDetailForm.controls[control].disable();
        }
      });
    }

    this.isSubmitted.subscribe((event) => {
      if (event) {
        this.setFieldsAsRequired();
        this.membershipDetailForm.markAllAsTouched();
        this.cdRef.markForCheck();
      }
    });

    this.membershipDetailForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.cdRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  writeValue(val: any): void {
    if (!!val) {
      this.membershipDetailForm.setValue(val, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.membershipDetailForm.valueChanges.subscribe(fn);
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled
      ? this.membershipDetailForm.disable()
      : this.membershipDetailForm.enable();
  }

  // @ts-ignore
  validate(c: AbstractControl): Observable<ValidationErrors> | null {
    // @ts-ignore
    return of(
      this.membershipDetailForm.valid
        ? null
        : { invalidMembershipDetailForm: true },
    );
  }

  // Before submitting the form, do not show error messages yet - allow user to submit first before
  // showing these errors
  private setFieldsAsRequired(): void {
    const { firstName, lastName, dateOfBirth } =
      this.membershipDetailForm.controls;

    firstName.setValidators([
      Validators.required,
      Validators.pattern(NAME_VALIDATION_REGEX),
    ]);
    lastName.setValidators([
      Validators.required,
      Validators.pattern(NAME_VALIDATION_REGEX),
    ]);

    if (dateOfBirth) {
      dateOfBirth.setValidators([Validators.required, dateOfBirthValidator()]);
      dateOfBirth.updateValueAndValidity();
    }

    firstName.updateValueAndValidity();
    lastName.updateValueAndValidity();
  }

  private getInitialFields(): TransferConnectV2MembershipDetailFormData {
    if (this.formData) {
      return {
        firstName: this.formData.firstName,
        membershipNumber: this.formData.membershipNumber,
        lastName: this.formData.lastName,
        dateOfBirth: dayjs(this.formData.dateOfBirth).format('YYYY/MM/DD'),
      };
    }

    return {
      // @ts-ignore
      firstName: this.prefillForm && this.user ? this.user.firstName : '',
      membershipNumber: '',
      // @ts-ignore
      lastName: this.prefillForm && this.user ? this.user.lastName : '',
      dateOfBirth: '',
    };
  }

  /*
  ====================================
    Form Error Checkers
  ====================================
  */

  isMembershipNumberInvalid(): boolean {
    return (
      this.membershipDetailForm.get('membershipNumber') as AbstractControl
    ).hasError('membershipNumberInvalid');
  }

  isMembershipNumberEmpty(): boolean {
    return (
      this.membershipDetailForm.get('membershipNumber') as AbstractControl
    ).hasError('required');
  }

  isFirstNameEmpty(): boolean {
    return (
      this.membershipDetailForm.get('firstName') as AbstractControl
    ).hasError('required');
  }

  firstNameIsInvalid(): boolean {
    return (
      this.membershipDetailForm.get('firstName') as AbstractControl
    ).hasError('pattern');
  }

  isLastNameEmpty(): boolean {
    return (
      this.membershipDetailForm.get('lastName') as AbstractControl
    ).hasError('required');
  }

  lastNameIsInvalid(): boolean {
    return (
      this.membershipDetailForm.get('lastName') as AbstractControl
    ).hasError('pattern');
  }

  dateOfBirthIsBlank(): boolean {
    const dateOfBirth = this.membershipDetailForm.get(
      'dateOfBirth',
    ) as AbstractControl;
    return dateOfBirth.touched && dateOfBirth.hasError('required');
  }

  dateOfBirthHasFutureDate(): boolean {
    return (
      this.membershipDetailForm.get('dateOfBirth') as AbstractControl
    ).hasError('futureDate');
  }

  /*
    ====================================
  */

  membershipNumberValidationPending(): boolean {
    return (
      this.membershipDetailForm.get('membershipNumber') as AbstractControl
    ).pending;
  }

  private membershipNumberValidator(): AsyncValidatorFn {
    const DEBOUNCE_TIMER = 500;

    // @ts-ignore
    return (
      input: UntypedFormControl,
    ): Observable<null | { membershipNumberInvalid: boolean }> => {
      if (
        !this.membershipDetailForm ||
        !(this.membershipDetailForm.get('membershipNumber') as AbstractControl)
          .value
      ) {
        return of(null);
      }

      input.markAsTouched(); // So that the error message shows up immediately without the user tabbing out
      return timer(DEBOUNCE_TIMER).pipe(
        takeUntil(this.destroy$),
        switchMap(() =>
          this.loyaltyProgramApiService.postMembershipValidation(
            this.loyaltyProgram.id,
            input.value,
          ),
        ),
        map((res) => {
          this.forceParentChange();
          return res === true ? null : { membershipNumberInvalid: true };
        }),
        catchError(() => of({ membershipNumberInvalid: true })),
        first(),
      );
    };
  }

  // Angular cannot detect form status changes in child async validators, therefore we need to
  // manually invoke the parent's change through the function we register on the CVA interface.
  private forceParentChange(): void {
    Promise.resolve().then(() =>
      this.propagateChange(this.membershipDetailForm.value),
    );
  }
}
